/**
 * Items and equipment. Adapted from opencode-archive item YAMLs.
 *
 * Each item has stat modifiers that flow into a unit's effective stats once
 * equipped (see `effectiveStats` in units.ts). Items belong to a slot —
 * inventory items can only be equipped to their matching slot.
 */
export type EquipSlot = 'weapon' | 'helm' | 'chest' | 'gloves' | 'legs' | 'boots' | 'ring1' | 'ring2' | 'amulet';
export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export interface StatMods {
  maxHp?: number;
  attackDamage?: number;
  attackRange?: number;
  attackInterval?: number;  // negative = faster
  moveSpeed?: number;
  morale?: number;          // flat resolve bonus (reduces incoming morale damage)
  armor?: number;           // flat damage reduction
}

export interface ItemDef {
  id: string;
  name: string;
  slot: EquipSlot;
  rarity: ItemRarity;
  spriteKey: string;        // matches an `item_*` PNG already loaded
  mods: StatMods;
  flavor?: string;
}

export const ITEMS: Record<string, ItemDef> = {
  // Weapons
  iron_sword:   { id: 'iron_sword',    name: 'Iron Sword',    slot: 'weapon', rarity: 'common',   spriteKey: 'item_sword',  mods: { attackDamage: 4 } },
  battle_axe:   { id: 'battle_axe',    name: 'Battle Axe',    slot: 'weapon', rarity: 'uncommon', spriteKey: 'item_axe',    mods: { attackDamage: 7, attackInterval: 0.15 } },
  war_mace:     { id: 'war_mace',      name: 'War Mace',      slot: 'weapon', rarity: 'uncommon', spriteKey: 'item_mace',   mods: { attackDamage: 6, attackInterval: 0.2 } },
  dagger:       { id: 'dagger',        name: 'Dagger',        slot: 'weapon', rarity: 'common',   spriteKey: 'item_dagger', mods: { attackDamage: 2, attackInterval: -0.25 } },
  longbow:      { id: 'longbow',       name: 'Longbow',       slot: 'weapon', rarity: 'rare',     spriteKey: 'item_bow',    mods: { attackDamage: 5, attackRange: 1.5 } },
  oak_staff:    { id: 'oak_staff',     name: 'Oak Staff',     slot: 'weapon', rarity: 'common',   spriteKey: 'item_staff',  mods: { attackDamage: 3, attackRange: 0.5 } },

  // Armor
  light_armor:  { id: 'light_armor',   name: 'Leather Armor', slot: 'chest',  rarity: 'common',   spriteKey: 'item_armor_light',  mods: { maxHp: 10, armor: 2 } },
  medium_armor: { id: 'medium_armor',  name: 'Mail Hauberk',  slot: 'chest',  rarity: 'uncommon', spriteKey: 'item_armor_medium', mods: { maxHp: 22, armor: 4, moveSpeed: -0.2 } },
  heavy_armor:  { id: 'heavy_armor',   name: 'Plate Cuirass', slot: 'chest',  rarity: 'rare',     spriteKey: 'item_armor_heavy',  mods: { maxHp: 40, armor: 8, moveSpeed: -0.5 } },

  // Accessories
  strength_ring: { id: 'strength_ring', name: 'Ring of Strength', slot: 'ring1', rarity: 'uncommon', spriteKey: 'item_ring',    mods: { attackDamage: 3 } },
  resolve_ring:  { id: 'resolve_ring',  name: 'Ring of Resolve',  slot: 'ring1', rarity: 'uncommon', spriteKey: 'item_ring',    mods: { morale: 6 } },
  wisdom_amulet: { id: 'wisdom_amulet', name: 'Amulet of Wisdom', slot: 'amulet',rarity: 'rare',    spriteKey: 'item_amulet',  mods: { attackRange: 1, morale: 4 } },
  swift_bracers: { id: 'swift_bracers', name: 'Swift Bracers',    slot: 'gloves',rarity: 'uncommon',spriteKey: 'item_bracers', mods: { attackInterval: -0.2, moveSpeed: 0.3 } },
};

export const RARITY_TINT: Record<ItemRarity, number> = {
  common:    0xb5b1a4,
  uncommon:  0x6fb56f,
  rare:      0x6fa9ff,
  legendary: 0xffaa3a,
};

export const RARITY_TEXT: Record<ItemRarity, string> = {
  common:    '#b5b1a4',
  uncommon:  '#6fb56f',
  rare:      '#6fa9ff',
  legendary: '#ffaa3a',
};

/** Loot table for what an enemy drops on death, weighted by depth tier. */
export function rollDrop(rand: () => number, depth: number): ItemDef | null {
  if (rand() > Math.min(0.65, 0.25 + depth * 0.05)) return null;
  const pools: Record<number, string[]> = {
    1: ['iron_sword', 'dagger', 'oak_staff', 'light_armor'],
    2: ['iron_sword', 'dagger', 'battle_axe', 'war_mace', 'light_armor', 'strength_ring', 'oak_staff'],
    3: ['battle_axe', 'war_mace', 'longbow', 'medium_armor', 'strength_ring', 'resolve_ring', 'swift_bracers'],
    4: ['longbow', 'medium_armor', 'heavy_armor', 'wisdom_amulet', 'resolve_ring', 'swift_bracers'],
  };
  const tier = Math.min(4, Math.max(1, Math.ceil(depth / 2)));
  const pool = pools[tier];
  const id = pool[Math.floor(rand() * pool.length)];
  return ITEMS[id] ?? null;
}
